import { buildApiUrl } from '../config/api';
import { refreshAuthenticatedSession } from './apiClient';
import {
  ResearchStreamError,
  createResearchEventApplier,
  parseErrorCode,
  parseSseBlock,
  readSseBlocks,
  researchErrorMessage,
  researchResponseError,
  type ParsedSseEvent,
  type ResearchStreamSnapshot,
  type StagingLaneVariant,
} from './risResearchStream';

/**
 * Der Laufspeicher aller Bahnen (docs/superpowers/specs/2026-10-16-durable-research-runs.md, Oberfläche): eine
 * Recherche beginnt als Lauf am Server; die Oberfläche verfolgt ihn nur noch über seinen Ereignisstrom. Lösen der
 * Verbindung beendet nie den Lauf. Die Nutzlast beider Bahnen wertet derselbe Auswerter aus (createResearchEventApplier),
 * die der ausführlichen Schicht ihr eigener (createDetailEventApplier), gewählt beim Verfolgen.
 */

export const RESEARCH_RUNS_ENDPOINT = '/api/ris/runs';

export interface ResearchRunStartRequest {
  sessionId: string;
  clientMessageId: string;
  userQuery: string;
  /** Die Wahl auf der Bahn B (für Entwickler auch C und D); ohne Angabe Gemini. Die Bahn A ruht (19.10.2026). */
  laneB?: { variant: StagingLaneVariant; modelPreset?: string; attachmentIds?: string[] };
}

/** Die ausführliche Schicht einer gespeicherten Antwort als Lauf (Ticket 09). */
export interface ResearchDetailRunStartRequest {
  sessionId: string;
  clientMessageId: string;
  targetMessageId: string;
}

/** „Wiederholen“: ein neuer Lauf zur Frage eines endgültigen Laufs ohne Antwort; Frage und Bahn nimmt der Server. */
export interface ResearchRunRetryRequest {
  sessionId: string;
  retryOf: string;
}

/** Der Endzustand eines Laufs ohne Antwort, wie ihn der Chat unter der Frage zeigt. */
export interface ResearchRunEnd {
  runId: string;
  state: 'failed' | 'cancelled' | 'interrupted';
  /** Öffentlicher Fehlercode eines fehlgeschlagenen Laufs, etwa TIMEOUT. */
  failureCode?: string | null;
  /** Der Satz des Servers zum Fehlschlag (run.failed data.error), etwa der des Relays; ohne ihn der allgemeine Status. */
  failureMessage?: string;
  /** Bahn und Variante des Laufs: „Wiederholen“ verfolgt den neuen Lauf mit demselben Auswerter. */
  lane?: 'A' | 'B';
  variant?: StagingLaneVariant | null;
}

/** Ein Lauf endete ohne Antwort: abgebrochen, fehlgeschlagen oder unterbrochen. */
export class ResearchRunEndedError extends ResearchStreamError {
  readonly runEnd: ResearchRunEnd;

  constructor(runEnd: ResearchRunEnd, code: string, message: string, partialResult: ResearchStreamSnapshot) {
    super(code, message, partialResult);
    this.runEnd = runEnd;
  }
}

export interface ResearchRunStartResult {
  runId: string;
  state: string;
  reused: boolean;
  lastSeq: number;
}

/** Das Ende eines erfolgreichen Laufs: der Stand der Darstellung und die Antwortnachricht, die der Server schrieb. */
export interface ResearchRunOutcome<S extends RunSnapshot = ResearchStreamSnapshot> {
  runId: string;
  answerMessageId?: string;
  snapshot: S;
  /**
   * Das Ende stammt aus dem Zustand des Laufs, nicht aus seinem Endereignis (die Ereignisse waren schon gelöscht): der
   * Stand kann ein Teiltext sein, gültig ist die gespeicherte Antwortnachricht.
   */
  fromRunState?: true;
}

/** Was jeder Stand eines Auswerters trägt: den Hinweis unter der Antwort und nach einem Neustart den Neustart-Hinweis. */
export interface RunSnapshot {
  statusMessage: string;
  restartNotice?: string;
}

/** Ein Auswerter der Nutzlast eines Laufs; je Versuch ein neuer. */
export interface RunEventApplier<S extends RunSnapshot = ResearchStreamSnapshot> {
  apply(event: ParsedSseEvent | null): void;
  snapshot(): S;
  /** Der Fehler, den ein Ereignis `error` gemeldet hat. */
  failure(): { code: string; message: string } | null;
  /** Der Stand am Erfolg; darf werfen, dann gilt snapshot(). */
  finish(): S;
}

const TERMINAL_EVENT_TYPES = new Set(['run.succeeded', 'run.failed', 'run.cancelled', 'run.interrupted']);
/** Der Hinweis nach einem automatischen Neustart des Laufs (run.attempt_reset). */
export const RUN_RESTART_NOTICE = 'Die Recherche wurde nach einem Serverneustart neu gestartet. Der bisherige Zwischenstand wurde verworfen.';
/** Ein Lauf wartet am Server auf seinen Start, etwa über der Kapazitätsgrenze der Bahn B: ein Zustand, kein Fehler. */
export const RUN_QUEUED_STATUS = 'Die Recherche wartet auf ihren Start und beginnt automatisch …';
const RUN_FAILED_MESSAGE = 'Die Recherche ist fehlgeschlagen. Bitte versuchen Sie es erneut.';
const RUN_END_MESSAGES: Readonly<Record<string, string>> = {
  'run.cancelled': 'Die Recherche wurde abgebrochen.',
  'run.interrupted': 'Die Recherche wurde unterbrochen. Bitte versuchen Sie es erneut.',
};
const RUN_FAILURE_MESSAGES: Readonly<Record<string, string>> = {
  SAVE_FAILED: 'Die Antwort konnte nicht gespeichert werden. Bitte versuchen Sie es erneut.',
  TIMEOUT: 'Die Recherche hat ihre Höchstdauer überschritten (Zeitüberschreitung). Bitte versuchen Sie es erneut.',
  STREAM_INTERRUPTED: 'Die Recherche wurde beendet, bevor eine vollständige Antwort vorlag. Bitte versuchen Sie es erneut.',
  LANE_UNAVAILABLE: 'Die Recherche ist derzeit nicht verfügbar. Bitte versuchen Sie es später erneut.',
  LANE_A_DISABLED: 'Die Bahn A ist abgeschaltet. Bitte versuchen Sie es erneut; die Recherche läuft dann auf der Bahn B.',
  LANE_C_UNAVAILABLE: 'Bahn C ist nicht aktiv. Bitte wählen Sie ein anderes Modell.',
  LANE_D_STREAM_INCOMPLETE: 'Bahn D beendete die Antwort unvollständig. Bitte versuchen Sie es erneut.',
  LANE_D_IDENTITY_MISMATCH: 'Die Antwort nennt nicht das gewählte Cortecs-Modell. Bitte versuchen Sie es erneut.',
  SESSION_NOT_FOUND: 'Diese Recherche wurde nicht gefunden.',
  MESSAGE_STATE_CONFLICT: 'Die Frage wurde nicht eindeutig gespeichert. Bitte versuchen Sie es erneut.',
};
/** Ganze Familien von Codes; ihr Satz gilt, wenn der Server keinen eigenen mitschickt. */
const RUN_FAILURE_FAMILY_MESSAGES: ReadonlyArray<readonly [string, string]> = [
  ['ATTACHMENTS_', 'Die Anhänge dieser Frage konnten nicht verwendet werden. Bitte prüfen Sie die Anhänge und versuchen Sie es erneut.'],
  ['STAGING_LANE_B_', 'Die Recherche konnte nicht abgeschlossen werden. Bitte versuchen Sie es erneut.'],
];

function runFailureMessage(code: string): string {
  return RUN_FAILURE_MESSAGES[code]
    ?? RUN_FAILURE_FAMILY_MESSAGES.find(([prefix]) => code.startsWith(prefix))?.[1]
    ?? RUN_FAILED_MESSAGE;
}

/** Was der Speicher je Lauf behält: den letzten gesehenen Cursor, den Auswerter und das Ende. */
interface RunEntry<S extends RunSnapshot = ResearchStreamSnapshot> {
  lastSeq: number;
  applier: RunEventApplier<S>;
  /** Der Auswerter eines neuen Versuchs. */
  createApplier: (onUpdate: (snapshot: S) => void) => RunEventApplier<S>;
  listener: (snapshot: S) => void;
  /** Bahn und Variante, die der Endzustand eines Laufs ohne Antwort nennt („Wiederholen“ auf derselben Bahn). */
  lane?: Pick<ResearchRunEnd, 'lane' | 'variant'>;
  /** Der Lauf startete neu (run.attempt_reset): jeder weitere Stand trägt den Hinweis. */
  restarted: boolean;
  outcome?: { ok: true; value: ResearchRunOutcome<S> } | { ok: false; error: ResearchStreamError };
}

/**
 * Wie verfolgt wird: Bahn und Variante des Laufs. Der Auswerter bekommt die Variante nur bei C oder D, wie bisher; ohne
 * diese Wahl bleibt seine Bahn offen, bis der Server sie meldet.
 */
export interface ResearchRunFollowOptions<S extends RunSnapshot = ResearchStreamSnapshot> {
  lane?: 'A' | 'B';
  variant?: StagingLaneVariant;
  /** Ein anderer Auswerter als der der Recherche, etwa der der ausführlichen Schicht. */
  applier?: (onUpdate: (snapshot: S) => void) => RunEventApplier<S>;
}

type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

type Wait = (ms: number, signal: AbortSignal) => Promise<void>;

/** Ein Zeitgeber, der seine Aufhebung liefert. */
type Timer = (callback: () => void, ms: number) => () => void;

export interface ResearchRunStoreOptions {
  fetch?: FetchLike;
  /** Wartezeit zwischen Versuchen; Tests setzen eine Uhr ohne echte Zeit ein. */
  wait?: Wait;
  /** Zeitgeber der Lesepause eines Ereignisstroms; Tests lösen ihn selbst aus. */
  timer?: Timer;
  /** Die Uhr für das Alter des letzten empfangenen Stücks. */
  now?: () => number;
}

const RECONNECT_BASE_MS = 250;
const RECONNECT_MAX_MS = 5_000;
const FAILURES_BEFORE_POLLING = 3;
const POLL_INTERVAL_MS = 1_000;
const THROTTLE_WINDOW_MS = 5_000;
/**
 * So lange darf ein offener Ereignisstrom schweigen (drei Herzschläge des Servers zu 15 s), dann gilt die Verbindung als
 * tot (etwa nach dem Ruhezustand des Geräts) und der Speicher verbindet neu ab dem letzten Ereignis.
 */
const READ_IDLE_MS = 45_000;
/** Weckt der Tab (sichtbar, Netz wieder da), verbindet ein Strom neu, dessen letztes Stück älter als ein Herzschlag ist. */
const WAKE_STALE_MS = 15_000;
const MAX_THROTTLE_MS = 60_000;
/** So viele beendete Läufe behält der Speicher, damit ein erneutes Verfolgen ihr Ende ohne Verbindung liefert. */
const MAX_FINISHED_RUNS = 20;
const FINAL_RUN_STATES = new Set(['succeeded', 'failed', 'cancelled', 'interrupted']);
/** Ein Ereignisstrom, der zu lange schwieg; das Verfolgen verbindet neu. */
class StalledStreamError extends Error {}
/** Antworten, nach denen ein weiterer Versuch nichts mehr ändert: das Verfolgen endet. */
const ENDING_STATUSES = new Set([400, 403, 404]);

/**
 * Die Abweisungen der Lauf-API in Worten. Die Grenzen prüft allein der Server; die Oberfläche nennt sie nur. Der
 * Ratenbegrenzer antwortet ohne Code und auf Englisch.
 */
const RUN_REFUSAL_MESSAGES: Readonly<Record<string, string>> = {
  SESSION_ACTIVE: 'Für diese Recherche wird gerade eine Antwort oder eine ausführliche Fassung erstellt. Bitte warten Sie, bis sie fertig ist.',
  USER_LIMIT: 'Es laufen bereits fünf Recherchen oder ausführliche Fassungen. Bitte warten Sie, bis eine davon fertig ist.',
  RUN_NOT_RETRYABLE: 'Diese Recherche kann nicht mehr wiederholt werden.',
  HTTP_429: 'Zu viele Rechercheanfragen in kurzer Zeit. Bitte warten Sie einen Moment.',
};

/**
 * N-02: erwartete Abweisungen, die die Oberfläche in Worten nennt (Grenzen der Lauf-API, eine nicht aktive Bahn); sie sind
 * kein Fehler der Anwendung und gehen nicht als Fehler in die Konsole.
 */
const EXPECTED_REFUSAL_CODES: ReadonlySet<string> = new Set([
  ...Object.keys(RUN_REFUSAL_MESSAGES), 'LANE_UNAVAILABLE', 'LANE_C_UNAVAILABLE', 'LANE_D_UNAVAILABLE', 'LANE_D_PRESET_INVALID',
]);
export const isExpectedRefusal = (error: unknown): boolean => error instanceof ResearchStreamError && EXPECTED_REFUSAL_CODES.has(error.code);

async function runRefusal(response: Response): Promise<ResearchStreamError> {
  const error = await researchResponseError(response);
  const message = RUN_REFUSAL_MESSAGES[error.code];
  return message
    ? new ResearchStreamError(error.code, message, { answer: '', statusMessage: message, toolNames: [] })
    : error;
}

/**
 * Wie lange nach einer 429 zu warten ist: Retry-After in Sekunden, sonst das volle Fenster. Der Ratenbegrenzer zählt
 * je Adresse (in einer Kanzlei oft eine gemeinsame), ein früherer Versuch verlängerte nur die Sperre.
 */
function throttleDelay(response: Response): number {
  const seconds = Number(response.headers.get('Retry-After'));
  return Number.isFinite(seconds) && seconds > 0
    ? Math.min(seconds * 1_000, MAX_THROTTLE_MS)
    : THROTTLE_WINDOW_MS;
}

/** Der Start einer neuen Frage, stets auf der Bahn B; ohne Wahl mit Gemini. */
function startBody(request: ResearchRunStartRequest): Record<string, unknown> {
  const laneB = request.laneB ?? { variant: 'B' };
  return {
    kind: 'research',
    lane: 'B',
    sessionId: request.sessionId,
    clientMessageId: request.clientMessageId,
    userQuery: request.userQuery,
    variant: laneB.variant,
    // Nur die Bahn D nennt ein Modell; ohne es weist der Server sie ab.
    ...(laneB.variant === 'D' && laneB.modelPreset ? { modelPreset: laneB.modelPreset } : {}),
    ...(laneB.attachmentIds?.length ? { attachmentIds: laneB.attachmentIds } : {}),
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function abortError(): DOMException {
  return new DOMException('The operation was aborted.', 'AbortError');
}

/**
 * Weckt die Leser der Läufe, sobald der Tab wieder sichtbar oder das Netz wieder erreichbar ist: wartende Leser verbinden
 * sofort neu, statt ihren Backoff abzuwarten. Liefert die Abmeldung der Zuhörer.
 */
export function wakeOnReturn(
  wake: () => void,
  doc: Pick<Document, 'visibilityState' | 'addEventListener' | 'removeEventListener'> = document,
  win: Pick<Window, 'addEventListener' | 'removeEventListener'> = window,
): () => void {
  const returned = () => {
    if (doc.visibilityState !== 'hidden') wake();
  };
  doc.addEventListener('visibilitychange', returned);
  win.addEventListener('online', returned);
  return () => {
    doc.removeEventListener('visibilitychange', returned);
    win.removeEventListener('online', returned);
  };
}

export class ResearchRunStore {
  private readonly fetchImpl: FetchLike;
  private readonly waitImpl: Wait;
  private readonly timerImpl: Timer;
  private readonly nowImpl: () => number;
  private readonly runs = new Map<string, RunEntry<any>>();
  /** Die beendeten Läufe in der Reihenfolge ihres Endes; der älteste fällt zuerst. */
  private readonly finished: string[] = [];
  private readonly wakers = new Set<() => void>();

  constructor(options: ResearchRunStoreOptions = {}) {
    this.fetchImpl = options.fetch ?? ((input, init) => fetch(input, init));
    this.waitImpl = options.wait ?? ((ms, signal) => this.sleep(ms, signal));
    this.timerImpl = options.timer ?? ((callback, ms) => {
      const handle = setTimeout(callback, ms);
      return () => clearTimeout(handle);
    });
    this.nowImpl = options.now ?? (() => Date.now());
  }

  /** Beendet laufende Wartezeiten sofort, etwa wenn der Tab wieder sichtbar oder das Netz wieder da ist. */
  wake(): void {
    for (const waker of [...this.wakers]) waker();
  }

  /**
   * Startet einen Lauf der Bahn B; die ruhende Bahn A startet keinen mehr. Dieselbe Client-Nachrichten-ID liefert
   * denselben Lauf (`reused`); ein Wiederholen desselben Laufs ebenso, ein Doppelklick startet also nur einen. Ein Lauf der Bahn B kann über ihrer
   * Kapazitätsgrenze `queued` bleiben; er beginnt, sobald ein Platz frei wird.
   */
  async start(request: ResearchRunStartRequest | ResearchRunRetryRequest, signal?: AbortSignal): Promise<ResearchRunStartResult> {
    const response = await this.request(RESEARCH_RUNS_ENDPOINT, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify('retryOf' in request
        ? { kind: 'research', sessionId: request.sessionId, retryOf: request.retryOf }
        : startBody(request)),
      signal,
    });
    if (!response.ok) throw await runRefusal(response);
    const payload: unknown = await response.json();
    if (!isRecord(payload) || typeof payload.runId !== 'string') {
      const message = 'Die Recherche konnte nicht gestartet werden.';
      throw new ResearchStreamError('RUN_START_INVALID', message, { answer: '', statusMessage: message, toolNames: [] });
    }
    return {
      runId: payload.runId,
      state: typeof payload.state === 'string' ? payload.state : 'queued',
      reused: payload.reused === true,
      lastSeq: typeof payload.lastSeq === 'number' ? payload.lastSeq : 0,
    };
  }

  /**
   * Startet die ausführliche Schicht einer Antwort als Lauf. Ist sie schon gespeichert, liefert der Server sie sofort ohne
   * Lauf (`stored`, die Nutzlast von detail_done).
   */
  async startDetail(
    request: ResearchDetailRunStartRequest,
    signal?: AbortSignal,
  ): Promise<ResearchRunStartResult | { stored: Record<string, unknown> }> {
    const response = await this.request(RESEARCH_RUNS_ENDPOINT, {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ kind: 'detail', ...request }),
      signal,
    });
    if (!response.ok) throw await runRefusal(response);
    const payload: unknown = await response.json();
    if (isRecord(payload) && payload.stored === true && isRecord(payload.detail)) return { stored: payload.detail };
    if (!isRecord(payload) || typeof payload.runId !== 'string') {
      const message = 'Die ausführliche Antwort konnte nicht gestartet werden.';
      throw new ResearchStreamError('RUN_START_INVALID', message, { answer: '', statusMessage: message, toolNames: [] });
    }
    return {
      runId: payload.runId,
      state: typeof payload.state === 'string' ? payload.state : 'queued',
      reused: payload.reused === true,
      lastSeq: typeof payload.lastSeq === 'number' ? payload.lastSeq : 0,
    };
  }

  /**
   * Bricht einen Lauf am Server ab, auch einen, den ein anderes Gerät gestartet hat. Sein Ende (run.cancelled oder ein
   * vorher festgeschriebener Erfolg) liefert danach der Ereignisstrom.
   */
  async cancel(runId: string): Promise<{ state: string }> {
    const response = await this.request(`${RESEARCH_RUNS_ENDPOINT}/${encodeURIComponent(runId)}/cancel`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw await runRefusal(response);
    const payload: unknown = await response.json();
    return { state: isRecord(payload) && typeof payload.state === 'string' ? payload.state : 'running' };
  }

  /**
   * Verfolgt einen Lauf ab dem letzten Ereignis, das dieser Speicher von ihm kennt (sonst ab 0 mit vollem
   * Nachspielen). Abbruch über `signal` löst nur die Verbindung; der Lauf am Server bleibt unberührt.
   */
  async follow<S extends RunSnapshot = ResearchStreamSnapshot>(
    runId: string,
    onUpdate: (snapshot: S) => void,
    signal: AbortSignal,
    options: ResearchRunFollowOptions<S> = {},
  ): Promise<ResearchRunOutcome<S>> {
    const entry = this.entry<S>(runId, options);
    entry.listener = onUpdate;
    const ended = (): ResearchRunOutcome<S> => {
      const outcome = entry.outcome!;
      if (outcome.ok) return outcome.value;
      throw outcome.error;
    };
    if (entry.outcome) return ended();
    let failures = 0;
    while (true) {
      if (signal.aborted) throw abortError();
      let throttled: number | undefined;
      try {
        const attempt = await this.connect(runId, entry, signal);
        if (attempt.received) failures = 0;
        throttled = attempt.throttled;
      } catch (error) {
        if (signal.aborted || (error instanceof DOMException && error.name === 'AbortError')) throw abortError();
        if (error instanceof ResearchStreamError) throw error;
        // Netzfehler und abgerissene Verbindungen: ein weiterer Versuch.
      }
      if (entry.outcome) {
        this.markFinished(runId);
        return ended();
      }
      if (throttled !== undefined) {
        // Ratenbegrenzer oder zu viele offene Ströme: das ganze Fenster abwarten, ohne Zustandsabfragen.
        await this.waitImpl(throttled, signal);
        continue;
      }
      failures += 1;
      const delay = Math.min(RECONNECT_BASE_MS * 2 ** (failures - 1), RECONNECT_MAX_MS);
      if (failures < FAILURES_BEFORE_POLLING) {
        await this.waitImpl(delay, signal);
      } else {
        await this.pollState(runId, entry, delay, signal);
        if (entry.outcome) {
          this.markFinished(runId);
          return ended();
        }
      }
    }
  }

  /**
   * Nach drei Fehlschlägen: je Sekunde den Zustand abfragen, bis der Backoff abgelaufen ist. Hat der Lauf ein
   * Ereignis oder sein Ende hinter dem Cursor, geht es sofort zurück zum Ereignisstrom. Ist er endgültig und kommt kein
   * Ereignis mehr (nach der Aufbewahrungsfrist gelöscht), stammt sein Ende aus dem Zustand des Laufs.
   */
  private async pollState(runId: string, entry: RunEntry<any>, delay: number, signal: AbortSignal): Promise<void> {
    for (let waited = 0; waited < delay; waited += POLL_INTERVAL_MS) {
      try {
        const response = await this.request(`${RESEARCH_RUNS_ENDPOINT}/${encodeURIComponent(runId)}`, {
          method: 'GET',
          headers: { Accept: 'application/json' },
          signal,
        });
        if (ENDING_STATUSES.has(response.status)) throw await this.endingError(response, entry);
        if (response.status === 429) {
          await this.waitImpl(throttleDelay(response), signal);
          return;
        }
        if (response.ok) {
          const state: unknown = await response.json();
          if (isRecord(state) && typeof state.lastSeq === 'number' && state.lastSeq > entry.lastSeq) return;
          if (isRecord(state) && typeof state.state === 'string' && FINAL_RUN_STATES.has(state.state)) {
            if (typeof state.lastSeq !== 'number') return;
            this.apply(runId, entry, `run.${state.state}`, {
              ...(typeof state.failureCode === 'string' ? { code: state.failureCode } : {}),
              ...(typeof state.answerMessageId === 'string' ? { answerMessageId: state.answerMessageId } : {}),
            });
            if (entry.outcome?.ok) entry.outcome = { ok: true, value: { ...entry.outcome.value, fromRunState: true } };
            return;
          }
        }
      } catch (error) {
        if (signal.aborted || (error instanceof DOMException && error.name === 'AbortError')) throw abortError();
        if (error instanceof ResearchStreamError) throw error;
      }
      await this.waitImpl(POLL_INTERVAL_MS, signal);
    }
  }

  private async endingError(response: Response, entry: RunEntry<any>): Promise<ResearchStreamError> {
    const refusal = await runRefusal(response);
    const message = response.status === 404 ? 'Die Recherche wurde nicht gefunden.' : refusal.message;
    return this.endError(refusal.code, message, entry);
  }

  private sleep(ms: number, signal: AbortSignal): Promise<void> {
    if (signal.aborted) return Promise.reject(abortError());
    return new Promise((resolve, reject) => {
      const settle = () => {
        clearTimeout(timer);
        signal.removeEventListener('abort', aborted);
        this.wakers.delete(done);
      };
      const done = () => {
        settle();
        resolve();
      };
      const aborted = () => {
        settle();
        reject(abortError());
      };
      const timer = setTimeout(done, ms);
      signal.addEventListener('abort', aborted, { once: true });
      this.wakers.add(done);
    });
  }

  private entry<S extends RunSnapshot>(runId: string, options: ResearchRunFollowOptions<S>): RunEntry<S> {
    let entry: RunEntry<S> | undefined = this.runs.get(runId);
    if (!entry) {
      // Die Variante C oder D der Bahn B; der Auswerter eines neuen Versuchs braucht sie wieder.
      const variant = options.variant === 'C' || options.variant === 'D' ? options.variant : undefined;
      const research = (onUpdate: (snapshot: ResearchStreamSnapshot) => void): RunEventApplier => (
        createResearchEventApplier(onUpdate, variant ? { variant } : {}));
      // Ohne eigenen Auswerter ist der Stand der der Recherche.
      const createApplier = options.applier ?? (research as unknown as RunEntry<S>['createApplier']);
      const created: RunEntry<S> = {
        lastSeq: 0,
        applier: createApplier(() => undefined),
        createApplier,
        listener: () => undefined,
        ...(options.lane ? { lane: { lane: options.lane, ...(options.lane === 'B' ? { variant: options.variant ?? 'B' } : {}) } } : {}),
        restarted: false,
      };
      created.applier = this.applierFor(created);
      entry = created;
      this.runs.set(runId, entry);
    }
    return entry;
  }

  /** Der Auswerter eines Versuchs; nach einem Neustart trägt jeder Stand den Hinweis. */
  private applierFor<S extends RunSnapshot>(entry: RunEntry<S>): RunEventApplier<S> {
    return entry.createApplier(snapshot => entry.listener(this.shown(entry, snapshot)));
  }

  private shown<S extends RunSnapshot>(entry: RunEntry<S>, snapshot: S): S {
    return entry.restarted ? { ...snapshot, restartNotice: RUN_RESTART_NOTICE } : snapshot;
  }

  private markFinished(runId: string): void {
    if (this.finished.includes(runId)) return;
    this.finished.push(runId);
    while (this.finished.length > MAX_FINISHED_RUNS) this.runs.delete(this.finished.shift()!);
  }

  /** Eine Verbindung zum Ereignisstrom: ob sie ein Ereignis brachte, und nach einer 429 die Wartezeit. */
  private async connect(
    runId: string,
    entry: RunEntry<any>,
    signal: AbortSignal,
  ): Promise<{ received: boolean; throttled?: number }> {
    // Die Verbindung hat ein eigenes Signal: eine Lesepause beendet nur sie, ein Abbruch von außen auch sie.
    const connection = new AbortController();
    const abortConnection = () => connection.abort();
    signal.addEventListener('abort', abortConnection, { once: true });
    let cancelIdle = () => {};
    let stall = () => {};
    let lastChunkAt = this.nowImpl();
    const wakeStale = () => {
      if (this.nowImpl() - lastChunkAt > WAKE_STALE_MS) stall();
    };
    try {
      const response = await this.request(`${RESEARCH_RUNS_ENDPOINT}/${encodeURIComponent(runId)}/events`, {
        method: 'GET',
        headers: { Accept: 'text/event-stream', 'Last-Event-ID': String(entry.lastSeq) },
        signal: connection.signal,
      });
      if (ENDING_STATUSES.has(response.status)) throw await this.endingError(response, entry);
      if (response.status === 429) return { received: false, throttled: throttleDelay(response) };
      if (!response.ok || !response.body) return { received: false };
      // Jedes Stück (auch der Herzschlag) setzt die Lesepause zurück; schweigt der Strom zu lange, endet er als tot.
      const idle = () => {
        cancelIdle();
        cancelIdle = this.timerImpl(() => stall(), READ_IDLE_MS);
      };
      const body = response.body.pipeThrough(new TransformStream<Uint8Array, Uint8Array>({
        start: (controller) => {
          stall = () => {
            controller.error(new StalledStreamError());
            connection.abort();
          };
          idle();
        },
        transform: (chunk, controller) => {
          lastChunkAt = this.nowImpl();
          idle();
          controller.enqueue(chunk);
        },
      }));
      this.wakers.add(wakeStale);
      let received = false;
      try {
        await this.readEvents(runId, entry, body, () => { received = true; });
      } catch (error) {
        if (!(error instanceof StalledStreamError) || signal.aborted) throw error;
      }
      return { received };
    } finally {
      cancelIdle();
      this.wakers.delete(wakeStale);
      signal.removeEventListener('abort', abortConnection);
    }
  }

  /** Liest die Ereignisse eines Stroms ab dem Cursor; meldet jedes neue Ereignis über `onReceived`. */
  private async readEvents(
    runId: string,
    entry: RunEntry<any>,
    body: ReadableStream<Uint8Array>,
    onReceived: () => void,
  ): Promise<void> {
    await readSseBlocks(body, (block) => {
      const parsed = parseSseBlock(block);
      if (!parsed || typeof parsed.payload.seq !== 'number') return false;
      const envelope = parsed.payload;
      const seq = envelope.seq as number;
      if (seq <= entry.lastSeq) return false;
      entry.lastSeq = seq;
      onReceived();
      const data = isRecord(envelope.data) ? envelope.data : {};
      return this.apply(runId, entry, parsed.type, data);
    }, () => this.endError('RESPONSE_TOO_LARGE', 'Die Rechercheantwort ist zu umfangreich und wurde aus Sicherheitsgründen abgebrochen. Bitte grenzen Sie Ihre Frage ein.', entry));
  }

  /** Ein Ereignis des Laufs; true beim Endereignis. */
  private apply(runId: string, entry: RunEntry<any>, type: string, data: Record<string, unknown>): boolean {
    if (type === 'run.attempt_reset') {
      // Der neue Versuch beginnt von vorn: Teiltext und Fortschritt des alten verfallen, der Hinweis bleibt bis zum Ende.
      entry.restarted = true;
      entry.applier = this.applierFor(entry);
      entry.listener(this.shown(entry, entry.applier.snapshot()));
      return false;
    }
    if (type === 'run.queued') {
      entry.listener({ ...this.shown(entry, entry.applier.snapshot()), statusMessage: RUN_QUEUED_STATUS });
      return false;
    }
    if (type === 'run.started') {
      entry.listener(this.shown(entry, entry.applier.snapshot()));
      return false;
    }
    if (!TERMINAL_EVENT_TYPES.has(type)) {
      entry.applier.apply({ type, payload: data });
      return false;
    }
    if (type === 'run.succeeded') {
      const answerMessageId = typeof data.answerMessageId === 'string' ? data.answerMessageId : undefined;
      entry.outcome = { ok: true, value: { runId, ...(answerMessageId ? { answerMessageId } : {}), snapshot: this.successSnapshot(entry) } };
      return true;
    }
    if (type === 'run.failed') {
      const code = parseErrorCode(data.code) || 'RUN_FAILED';
      // Der Satz am Ende des Laufs geht vor: Abweisungen der Abrechnung (runner.ts failureOf) und des Relays (laneB.ts,
      // detail.ts) tragen ihn in `error`, `message` bleibt Rückfall. Dann der eines Fehlerereignisses der Gegenseite, dann
      // der zum Code.
      const relayed = typeof data.error === 'string' && data.error.trim() ? data.error : data.message;
      const message = researchErrorMessage(code, relayed, entry.applier.failure()?.message || runFailureMessage(code));
      const failureCode = parseErrorCode(data.code) || null;
      const failureMessage = researchErrorMessage(code, relayed, '');
      entry.outcome = { ok: false, error: this.endError(code, message, entry, {
        runId, state: 'failed', failureCode, ...(failureMessage ? { failureMessage } : {}), ...entry.lane,
      }) };
      return true;
    }
    const cancelled = type === 'run.cancelled';
    const runEnd: ResearchRunEnd = { runId, state: cancelled ? 'cancelled' : 'interrupted', ...entry.lane };
    entry.outcome = { ok: false, error: this.endError(cancelled ? 'RUN_CANCELLED' : 'RUN_INTERRUPTED', RUN_END_MESSAGES[type], entry, runEnd) };
    return true;
  }

  /**
   * Der Stand am Erfolg, wie am Ende des alten Stroms: eine Antwort der Bahn B ohne Prüfergebnis gilt als
   * „Quellenprüfung nicht verfügbar“ (so speichert sie auch der Server).
   */
  private successSnapshot<S extends RunSnapshot>(entry: RunEntry<S>): S {
    try {
      return entry.applier.finish();
    } catch {
      return entry.applier.snapshot();
    }
  }

  private endError(code: string, message: string, entry: RunEntry<any>, runEnd?: ResearchRunEnd): ResearchStreamError {
    // Der Stand des Auswerters des Laufs; der der ausführlichen Schicht geht ebenso unverändert mit.
    const snapshot = { ...entry.applier.snapshot(), statusMessage: message } as ResearchStreamSnapshot;
    return runEnd ? new ResearchRunEndedError(runEnd, code, message, snapshot) : new ResearchStreamError(code, message, snapshot);
  }

  /** Eine Anfrage mit Cookie; eine abgelaufene Sitzung wird einmal erneuert und die Anfrage wiederholt. */
  private async request(endpoint: string, init: RequestInit): Promise<Response> {
    const requestInit: RequestInit = { credentials: 'include', ...init };
    const response = await this.fetchImpl(buildApiUrl(endpoint), requestInit);
    if (response.status !== 401) return response;
    if (init.signal?.aborted) throw abortError();
    await refreshAuthenticatedSession();
    if (init.signal?.aborted) throw abortError();
    return this.fetchImpl(buildApiUrl(endpoint), requestInit);
  }
}
